import xlsx, { ISettings, IJsonSheet, IColumn, IContent } from "json-as-xlsx";
import moment from "moment";
import {
  RelatorioDespesaItem,
  DespesaQuery,
  RelatorioDespesaFields,
  PDFRelatorioDespesasResponse,
} from "features/relatorios/typings";
import PDFRelatorioDespesasPaginaLinha from "../PDFRelatorio/PDFRelatorioDespesasPaginaLinha";
import { pdf, Document } from "@react-pdf/renderer";
import { mergePdfsFromBlobOrUrlList } from "infra/helpers/mergePdfsFromBlobOrUrl";
import PDFImagePage from "components/PDF/ImagePage";
import { getContaCategoriaText } from "./getContaCategoriaText";

export function exportExcelRelatorioDespesas({
  items,
  filtro,
  selectedFields,
}: {
  items: RelatorioDespesaItem[];
  filtro: DespesaQuery;
  selectedFields: RelatorioDespesaFields[];
}) {
  const settings: ISettings = {
    fileName: `Relatório de Despesas - ${moment(filtro.dataInicio).format(
      "DD_MM_YYYY"
    )} à ${moment(filtro.dataFim).format("DD_MM_YYYY")}`,
    extraLength: 3,
    writeOptions: {},
  };

  const columns: IColumn[] = [];

  if (selectedFields.includes("dataVencimento")) {
    columns.push({
      label: "Vencimento",
      value: (row: IContent) =>
        moment((row as any).dataVencimento).format("DD/MM/YYYY"),
    });
  }

  if (selectedFields.includes("dataPagamento")) {
    columns.push({
      label: "Pagamento",
      value: (row: IContent) =>
        (row as any).dataPagamento
          ? moment((row as any).dataPagamento).format("DD/MM/YYYY")
          : "",
    });
  }

  if (selectedFields.includes("pessoaNome")) {
    columns.push({
      label: "Favorecido",
      value: (row: IContent) => (row as any).pessoa.nome,
    });
  }

  if (selectedFields.includes("descricao")) {
    columns.push({
      label: "Descrição",
      value: "descricao",
    });
  }

  if (selectedFields.includes("documento")) {
    columns.push({
      label: "Documentos",
      value: "documento",
    });
  }

  if (selectedFields.includes("contaCategoria")) {
    columns.push({
      label: "Conta Categoria",
      value: (row: IContent) =>
        getContaCategoriaText((row as any).contasCategorias),
    });
  }

  if (selectedFields.includes("valor")) {
    columns.push({
      label: "Valor",
      format: "#,###0.00",
      value: (row: IContent) => (!row.valor ? "" : +row.valor),
    });
  }

  const contentMapper = (item: RelatorioDespesaItem) => {
    const obj: Record<keyof RelatorioDespesaItem, any> = {} as any;

    Object.keys(item).forEach((key) => {
      obj[key as keyof RelatorioDespesaItem] =
        item[key as keyof RelatorioDespesaItem] ?? "";
    });

    return obj;
  };

  const data: IJsonSheet[] = [
    {
      sheet: "Despesas",
      columns: columns,
      content: items.map(contentMapper),
    },
  ];

  xlsx(data, settings);
}

export async function getRelatorioDespesasBlobAndUrlsExportAnexos(
  data: PDFRelatorioDespesasResponse,
  selectedFields: RelatorioDespesaFields[],
  onProgress?: (current: number, total: number) => any
) {
  const blobAndUrlList: (Blob | string)[] = [];

  for (let i = 0; i < data.items.length; i++) {
    onProgress?.(i + 1, data.items.length);

    const item = data.items[i];
    const pdfLinhaBlob = await pdf(
      <PDFRelatorioDespesasPaginaLinha
        item={item}
        endereco1={data.endereco1}
        endereco2={data.endereco2}
        logo={data.logo}
        nomeTenant={data.nomeTenant}
        logoAdm={data.logoAdm}
        periodo={data.periodo}
        selectedFields={selectedFields}
        assinaturas={i === data.items.length - 1 ? data.assinaturas : undefined}
      />
    ).toBlob();

    blobAndUrlList.push(pdfLinhaBlob);
    if (item.anexos?.length) {
      for (const anexo of item.anexos) {
        if (anexo.mimetype.includes("pdf")) {
          blobAndUrlList.push(anexo.referencia);
        } else if (anexo.mimetype.includes("image")) {
          const pdfImageBlob = await pdf(
            <Document>
              <PDFImagePage source={anexo.referencia} />
            </Document>
          ).toBlob();

          blobAndUrlList.push(pdfImageBlob);
        }
      }
    }
  }

  return blobAndUrlList;
}

export async function getRelatorioDespesasComAnexosBlob(
  data: PDFRelatorioDespesasResponse,
  selectedFields: RelatorioDespesaFields[],
  onProgressGenerate?: (current: number, total: number) => any,
  onProgressMerge?: (current: number, total: number) => any
) {
  const blobAndUrlList = await getRelatorioDespesasBlobAndUrlsExportAnexos(
    data,
    selectedFields,
    onProgressGenerate
  );

  return await mergePdfsFromBlobOrUrlList(blobAndUrlList, onProgressMerge);
}
